<template>
  <WithBackdrop
    :class="[$style.config, $style.wrap]"
    :backdrop="data?.fields?.backdrop"
  >
    <div :class="$style.inner">
      <header v-if="viewMode === 'keyDates'" :class="$style.keyDatesHeader">
        <BaseHeading :class="$style.heading">
          {{ localizedStrings['key-dates'] || 'Key Dates' }}
        </BaseHeading>
      </header>

      <header v-if="viewMode === 'calendar'" :class="$style.calendarHeader">
        <BaseHeading :class="$style.heading">
          {{ data?.fields?.header }}
        </BaseHeading>
      </header>

      <div :class="$style.filters">
        <BaseSelect
          v-model="selectedBrandFilter"
          :class="$style.select"
          :placeholder="localizedStrings['Brand'] || 'Brand'"
          :options="computedBrands"
          :clearable="true"
          :hide-text-overflow="true"
        />
        <BaseSelect
          v-model="selectedProductFilter"
          :class="$style.select"
          :placeholder="localizedStrings['Product'] || 'Product'"
          :options="computedProducts"
          :clearable="true"
          :hide-text-overflow="true"
        />
        <SearchField
          v-if="viewMode === 'keyDates'"
          :class="$style.search"
          :placeholder="localizedStrings['search'] || 'Search'"
          @request-query="handleSearchKeyDates"
          @clear-query="handleSearchKeyDates"
        />
        <div v-if="viewMode === 'calendar'" :class="$style.checkboxInput">
          <input
            id="current-events"
            v-model="showCurrentEventsOnly"
            type="checkbox"
          />
          <label for="current-events">{{
            localizedStrings['showCurrentEventsOnly'] ||
            'Show Current Events Only'
          }}</label>
        </div>
        <div :class="$style.viewModes">
          <button
            :class="[$style.viewMode, viewMode === 'keyDates' && $style.active]"
            @click="setViewMode('keyDates')"
          >
            {{ localizedStrings['key-dates'] || 'Key Dates' }}
          </button>
          <button
            :class="[$style.viewMode, viewMode === 'calendar' && $style.active]"
            @click="setViewMode('calendar')"
          >
            {{ localizedStrings['calendar'] || 'Calendar' }}
          </button>
        </div>
      </div>

      <div v-if="viewMode === 'keyDates'" :class="$style.keyDates">
        <div v-if="keyDatesLoading" :class="$style.progress">
          <LoaderDots :aria-label="localizedStrings['loading'] || 'Loading'" />
        </div>
        <div v-else-if="!keyDatesResults.length" :class="$style.noResults">
          {{ localizedStrings['no-results'] }}
        </div>
        <div
          v-for="keyDate of keyDatesResults"
          :key="keyDate?.sys?.id"
          :class="$style.keyDate"
        >
          <div :class="$style.keyDateContent">
            <div :class="$style.calendarIcon">
              <div :class="$style.calendarMonth">
                {{
                  format(new Date(keyDate?.fields?.startDatetime || ''), 'LLL')
                }}
              </div>
              <div :class="$style.calendarDay">
                {{
                  format(new Date(keyDate?.fields?.startDatetime || ''), 'd')
                }}
              </div>
            </div>
            <div :class="$style.keyDateHeaderWrap">
              <BasePicture
                :key="keyDate?.fields?.icons?.[0]?.sys?.id"
                :src="`${keyDate?.fields?.icons?.[0]?.fields?.file?.url}?w=96`"
                alt=""
                :img-class="$style.eventIcon"
                loading="eager"
              />
              <div :class="$style.keyDateHeader">
                <div :class="$style.keyDateName">
                  {{ keyDate?.fields?.title }}
                </div>
                <div :class="$style.keyDateDescription">
                  {{ keyDate?.fields?.eventInformation }}
                </div>
              </div>
            </div>
          </div>
          <div :class="$style.keyDateLinks">
            <wpn-event-btn
              v-if="keyDate?.fields?.eventLink"
              :entry="keyDate?.sys?.id"
              presentation="outline"
              target="_blank"
            >
              {{ localizedStrings['schedule'] || 'Schedule' }}
            </wpn-event-btn>
            <!-- CallToAction has built-in push to dataLayer -->
            <CallToAction
              v-if="keyDate?.fields?.learnMoreLink"
              :parent-entry-title="keyDate?.fields?.entryTitle"
              :cta="buildKeyDateLearnMoreCta(keyDate)"
              :data-layer="keyDateLearnMoreDataLayer"
            />
          </div>
        </div>
        <CtaWings v-if="!keyDatesLoading && hasMoreKeyDates">
          <BaseButton @click="handleLoadMoreKeyDates">
            {{ localizedStrings['load-more'] || 'Load More' }}
          </BaseButton>
        </CtaWings>
      </div>

      <div v-if="viewMode === 'calendar'" :class="$style.monthHeaderLayout">
        <div :class="[$style.monthHeaderBlock, $style.monthHeaderBlockLeft]">
          <MarkedParser :md="data?.fields?.subheader" :class="$style.copy" />
        </div>
        <div :class="[$style.monthHeaderBlock, $style.monthHeaderBlockCenter]">
          <button :class="$style.monthPaging" @click="handlePreviousMonthClick">
            <ScreenReaderOnly>
              {{ localizedStrings['previous'] || 'Previous' }}
            </ScreenReaderOnly>
            <IconChevronLeft :class="$style.iconArrow" aria-hidden />
          </button>
          <BaseHeading :class="$style.monthLabel" size="h4">
            {{ computedMonthAndYearLabel }}
          </BaseHeading>
          <button :class="$style.monthPaging" @click="handleNextMonthClick">
            <ScreenReaderOnly>
              {{ localizedStrings['next'] || 'Next' }}
            </ScreenReaderOnly>
            <IconChevronRight :class="$style.iconArrow" aria-hidden />
          </button>
        </div>
        <div :class="[$style.monthHeaderBlock, $style.monthHeaderBlockRight]">
          <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />
        </div>
      </div>

      <div v-if="viewMode === 'calendar'" :class="$style.calendar">
        <div :class="$style.grid">
          <div
            v-for="a in localizedWeekDays"
            :key="a"
            :class="$style.dayHeader"
          >
            {{ a }}
          </div>
        </div>
        <div
          v-for="{ id, datesOfWeek, events, rows } in computedEventsPerWeek"
          :key="id"
          :class="[$style.grid, $style.week]"
          :style="{
            '--rows': rows,
          }"
        >
          <div
            v-for="dayOfWeek in datesOfWeek"
            :key="dayOfWeek.date"
            :class="[
              $style.day,
              !dayOfWeek.isSameMonth && $style.differentMonth,
            ]"
          >
            <span :class="[$style.dayTag, dayOfWeek.isToday && $style.today]">
              {{ dayOfWeek.date }}
            </span>
          </div>
          <CalendarEvent
            v-for="e in events"
            :key="e?.id"
            :class="$style.event"
            :data="e?.event"
            :disabled="e.hasEnded"
            :is-active="
              activeEventPath === e?.event?.fields?.pathOverride ||
              activeEventPath === e?.event?.fields?.path
            "
            :style="{
              width: `${((e?.duration || 0) / 7) * 100}%`,
              left: `${((e?.firstDayInWeekIndex || 0) / 7) * 100}%`,
              top: `calc(${(e?.row || 0) + 1} * var(--calendar-event-height))`,
            }"
            @mouseenter="
              () =>
                onMouseenter(
                  e?.event?.fields?.path ||
                    e?.event?.fields?.pathOverride ||
                    '',
                )
            "
            @mouseleave="onMouseleave"
          />
        </div>
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import {
  add,
  sub,
  isPast,
  startOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  getMonth,
  getYear,
  getDate,
  closestTo,
  lastDayOfWeek,
  eachDayOfInterval,
  eachWeekOfInterval,
  isSameMonth,
  isSameDay,
  differenceInCalendarDays,
  getDay,
  format,
} from 'date-fns'
import { fetchAccessibleEventIds } from '~/services/events'

import type {
  TypeCallToActionWithoutUnresolvableLinksResponse,
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeEventSkeleton,
  TypeEventWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

import { DAYS_OF_THE_WEEK } from '~/utils/const'
import formatAsContentfulDate from '~/utils/formatAsContentfulDate'

const localizedWeekDays = useLocalizedCalendarWeekDays()

export interface ICalendarEvent {
  id: string
  days: IDaysOfTheWeek[] | undefined
  startDatetime: string | Date | undefined
  endDatetime: string | Date | undefined
  oneDayEvent?: boolean
  duration?: number
  firstDayInWeekIndex?: number
  hasEnded?: boolean
  path?: string
  pathOverride?: string
  row?: number
  event: TypeEventWithoutUnresolvableLinksResponse
}

const { localizedStrings } = useLocalizedStrings()

const userStore = useUserStore()
const { highestUserLevel } = storeToRefs(userStore)
const { locale } = useI18n()
const { $client, $visibility, $userAccessCheck } = useNuxtApp()

defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

// Data
const viewMode = ref<'keyDates' | 'calendar'>('keyDates')
const searchQuery = ref('')
const currentPage = ref(1)

const selectedBrandFilter = ref('')
const selectedProductFilter = ref('')
const showCurrentEventsOnly = ref(false)

const date = ref<Date | ''>('')
const activeEventPath = ref('')

const KEY_DATES_LIMIT = 5
const KEY_DATE_LEARN_MORE_EVENT = 'schedule_learn_more_click'

const keyDateLearnMoreDataLayer = {
  event: 'ga4.trackEvent',
  eventName: KEY_DATE_LEARN_MORE_EVENT,
}

const buildKeyDateLearnMoreCta = (
  keyDate: TypeEventWithoutUnresolvableLinksResponse,
) =>
  ({
    fields: {
      label: localizedStrings.value['learn-more'] || 'Learn More',
      link: keyDate?.fields?.learnMoreLink,
      entryTitle: KEY_DATE_LEARN_MORE_EVENT,
      target: '_blank',
    },
  }) as TypeCallToActionWithoutUnresolvableLinksResponse

const keyDatesResults = ref<TypeEventWithoutUnresolvableLinksResponse[]>([])
const keyDatesLoading = ref(true)
const keyDatesTotal = ref(0)

const { data: eventsResponse } = await useAsyncData(
  'events-calendar',
  async () => {
    if (!date.value) {
      return {
        events: [],
        brands: [],
        products: [],
      }
    }

    const startDay = startOfWeek(startOfMonth(date.value))
    const endDay = endOfWeek(endOfMonth(date.value))

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        include: 10,
        limit: 500,
        locale: locale.value,
        'fields.startDatetime[lte]': formatAsContentfulDate(endDay),
        'fields.endDatetime[gte]': formatAsContentfulDate(startDay),
        order: ['fields.startDatetime'],
        select: [
          'sys.id',
          'fields.entryTitle',
          'fields.days',
          'fields.icons',
          'fields.title',
          'fields.path',
          'fields.pathOverride',
          'fields.color',
          'fields.backgroundColor',
          'fields.startDatetime',
          'fields.endDatetime',
          'fields.visibility',
          'fields.showOnlyInCalendar',
          'fields.accessLevel',
          'fields.associatedBrand',
          'fields.associatedProduct',
        ],
      })

    const filtered = response.items.filter((event) => {
      const hasAccess = $userAccessCheck(
        event.fields?.accessLevel,
        highestUserLevel.value,
      )
      const isVisible = $visibility(event.fields?.visibility)
      const hasSelectedBrand = selectedBrandFilter.value
        ? event?.fields?.associatedBrand?.sys?.id === selectedBrandFilter.value
        : true
      const hasSelectedProduct = selectedProductFilter.value
        ? event?.fields?.associatedProduct?.sys?.id ===
          selectedProductFilter.value
        : true
      const isCurrent = showCurrentEventsOnly.value
        ? isPast(new Date(event?.fields?.startDatetime || '')) &&
          !isPast(new Date(event?.fields?.endDatetime || ''))
        : true
      return (
        hasAccess &&
        isVisible &&
        hasSelectedBrand &&
        hasSelectedProduct &&
        isCurrent
      )
    })

    // Get all brands for filtering
    const uniqueBrands = response.items.reduce<{ [key: string]: string }>(
      (accu, curr) => {
        const brand = curr.fields?.associatedBrand
        if (brand) {
          const value = brand?.sys?.id
          const label = brand?.fields?.label
          if (value && label) accu[value] = label
        }
        return accu
      },
      {},
    )
    const brands = Object.entries(uniqueBrands).map(([key, value]) => ({
      label: value,
      value: key,
    }))

    // Get all products for filtering
    const uniqueProducts = response.items.reduce<{ [key: string]: string }>(
      (accu, curr) => {
        const product = curr.fields?.associatedProduct
        if (product) {
          const value = product?.sys?.id
          const label = product?.fields?.name
          if (value && label) accu[value] = label
        }
        return accu
      },
      {},
    )
    const products = Object.entries(uniqueProducts).map(([key, value]) => ({
      label: value,
      value: key,
    }))

    return {
      events: filtered,
      brands,
      products,
    }
  },
  {
    transform(input) {
      return {
        events: input.events,
        fetchedAt: useUnixTimestamp(),
        brands: input.brands,
        products: input.products,
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [
      date,
      selectedBrandFilter,
      selectedProductFilter,
      showCurrentEventsOnly,
    ],
    server: false,
    immediate: false,
  },
)

const { data: keyDatesResponse, execute: fetchKeyDates } = await useAsyncData(
  `key-dates-${searchQuery.value}-${currentPage.value}`,
  async () => {
    const today = startOfDay(new Date())

    const accessibleEventIds = await fetchAccessibleEventIds({
      $client,
      $userAccessCheck,
      $visibility,
      locale: locale.value,
      highestUserLevel: highestUserLevel.value,
      searchQuery: searchQuery.value,
      brandId: selectedBrandFilter.value || undefined,
      productId: selectedProductFilter.value || undefined,
      queryParams: {
        'fields.showInKeyDates': true,
        'fields.startDatetime[gte]': formatAsContentfulDate(today),
      },
    })

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        include: 2,
        limit: KEY_DATES_LIMIT * currentPage.value,
        order: ['fields.startDatetime'],
        'sys.id[in]': accessibleEventIds.entryIds,
        select: [
          'sys.id',
          'fields.entryTitle',
          'fields.icons',
          'fields.title',
          'fields.startDatetime',
          'fields.visibility',
          'fields.accessLevel',
          'fields.associatedBrand',
          'fields.associatedProduct',
          'fields.eventInformation',
          'fields.eventLink',
          'fields.learnMoreLink',
        ],
      })

    return {
      events: response.items,
      total: response.total,
      brands: accessibleEventIds.brands,
      products: accessibleEventIds.products,
    }
  },
  {
    transform(input) {
      keyDatesResults.value = input.events || []
      keyDatesTotal.value = input.total || 0
      keyDatesLoading.value = false
      return {
        events: input.events,
        fetchedAt: useUnixTimestamp(),
        brands: input.brands,
        products: input.products,
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [selectedBrandFilter, selectedProductFilter],
    server: false,
  },
)

const getEventDays = (event: ICalendarEvent) => {
  return Array.from(Array(event?.duration)).map(
    (_, index) => (event?.firstDayInWeekIndex || 0) + index,
  )
}

const getOccupiedDays = (row: ICalendarEvent[]) => {
  return row.reduce((rowSoFar: number[], event) => {
    const eventDays = getEventDays(event)
    return [...rowSoFar, ...eventDays]
  }, [])
}

// Compute
const computedBrands = computed(() => {
  const brands =
    viewMode.value === 'keyDates'
      ? keyDatesResponse.value?.brands
      : eventsResponse.value?.brands
  if (!brands) return []
  return [...brands].sort((a, b) => a.label.localeCompare(b.label))
})

const computedProducts = computed(() => {
  const products =
    viewMode.value === 'keyDates'
      ? keyDatesResponse.value?.products
      : eventsResponse.value?.products
  if (!products) return []
  return [...products].sort((a, b) => a.label.localeCompare(b.label))
})

const hasMoreKeyDates = computed(
  () => keyDatesTotal.value > keyDatesResults.value?.length,
)

const computedEventsPerWeek = computed(() => {
  const weeksInMonth = eachWeekOfInterval({
    start: startOfMonth(date.value),
    end: endOfMonth(date.value),
  })

  return weeksInMonth.map((weekDate) => {
    const startOfWeek = new Date(weekDate)
    const endOfWeek = lastDayOfWeek(startOfWeek)

    const daysOfWeek = eachDayOfInterval({
      start: startOfWeek,
      end: endOfWeek,
    })

    const datesOfWeek = daysOfWeek.map((dayDate) => getDate(dayDate))

    const simplifiedEvents = eventsResponse.value?.events?.map?.((event) => {
      return {
        event,
        id: event?.sys?.id,
        days: event?.fields?.days,
        startDatetime: event?.fields?.startDatetime,
        endDatetime: event?.fields?.endDatetime,
      }
    })

    // If needed we're going to break up long running events by days of
    // the week they're on. We want to this all at the beggining so the
    // rest of the weekly placement logic treats them the same as the
    // other events.
    const eventsByRow = simplifiedEvents
      ?.reduce((eventsSoFar: ICalendarEvent[], event: ICalendarEvent) => {
        // This logic only get's run if the entry has specific days of the
        // week set for the entry.

        if (event?.days) {
          const eventDays = eachDayOfInterval({
            start: new Date(event?.startDatetime || ''),
            end: new Date(event?.endDatetime || ''),
          })
            // if we have specific days of the week we want to filter out all the rest
            .filter((eventDay) => {
              // GetDay index in week and check if it matches the index of a day in eventDays
              return event?.days?.includes(DAYS_OF_THE_WEEK[getDay(eventDay)])
            })

          // We need events with specific days of the week grouped by days
          // in a row. So Tuesday, Wednesday, Thursday would be 1 event but
          // Tuesday, Thursday would be two separate events.
          const eventsBySequence = eventDays.reduce(
            (
              eventsBySequenceSoFar: ICalendarEvent[],
              eventDay: Date,
              eventDayIndex: number,
            ) => {
              const [previousEvent] = eventsBySequenceSoFar.slice(-1)
              // If this isn't the first day of an event AND the previous
              // day was also the previous calendar day
              if (
                previousEvent &&
                differenceInCalendarDays(
                  eventDay,
                  new Date(previousEvent?.endDatetime || ''),
                ) === 1
              ) {
                // Return events so far but update the previous event
                return [
                  ...eventsBySequenceSoFar.slice(
                    0,
                    eventsBySequenceSoFar.length - 1,
                  ),
                  {
                    ...previousEvent,
                    // Set this event day as the new end date time
                    endDatetime: eventDay,
                    // We know it's not a 1 day event now.
                    oneDayEvent: false,
                  },
                ]
              }
              // If no previous event OR the previous event was more than
              // a day ago we're dealing with the first day of an event
              // sequence again
              return [
                ...eventsBySequenceSoFar,
                {
                  event: event.event,
                  id: `${event?.id}-${eventDayIndex}`,
                  startDatetime: eventDay,
                  endDatetime: eventDay,
                  // Assume 1 day event. Change it later if need be.
                  oneDayEvent: true,
                  days: event?.days,
                },
              ]
            },
            [],
          )
          return [...eventsSoFar, ...eventsBySequence]
        }

        return [...eventsSoFar, event]
      }, [])
      .map((event) => {
        // initial hydaration of eventWeeks
        const eventWeeks = eachWeekOfInterval({
          start: new Date(event?.startDatetime || ''),
          end: new Date(event?.endDatetime || ''),
        })

        return {
          ...event,
          eventWeeks,
        }
      })
      .filter(({ eventWeeks }) =>
        // convert to strings to compare Dates
        eventWeeks.map(String).includes(String(weekDate)),
      )
      .map((event) => {
        const eventDays = eachDayOfInterval({
          start: new Date(event.startDatetime || ''),
          end: new Date(event.endDatetime || ''),
        })
        const closestToFirst = closestTo(startOfWeek, eventDays) || new Date()
        const firstDayInWeek = getDate(closestToFirst)
        const closestToLast = closestTo(endOfWeek, eventDays) || new Date()
        const lastDayInWeek = getDate(closestToLast)

        return {
          ...event,
          startOfWeek,
          endOfWeek,
          datesOfWeek,
          eventDays,
          hasEnded: isPast(new Date(event.endDatetime || '')),
          firstDayInWeekIndex: datesOfWeek.indexOf(firstDayInWeek),
          lastDayInWeekIndex: datesOfWeek.indexOf(lastDayInWeek),
          duration:
            // Calculate the number of days this event occurs during this specific week.
            differenceInCalendarDays(closestToLast, closestToFirst) + 1,
        }
      })
      .reduce(
        (eventRowsSoFar: ICalendarEvent[][], currentEvent: ICalendarEvent) => {
          // Get the "data points" of the current event
          const eventDays = getEventDays(currentEvent)

          // Loop through the existing rows for a space this event can fit
          for (let i = 0; i < eventRowsSoFar.length; i++) {
            const currentRow = eventRowsSoFar[i]

            // Get an array of all occupied spaces in this row
            const occupiedSpaces = getOccupiedDays(currentRow)

            // Are the spaces this event needs unoccupied?
            if (!eventDays.some((i) => occupiedSpaces.includes(i))) {
              return [
                ...eventRowsSoFar.slice(0, i),
                [...eventRowsSoFar[i], currentEvent],
                ...eventRowsSoFar.slice(i + 1),
              ]
            }
          }

          // If no rows are available, add a new one.
          return [...eventRowsSoFar, [currentEvent]]
        },
        [[]],
      )

    return {
      id: Number(weekDate),
      datesOfWeek: daysOfWeek.map((day) => ({
        date: getDate(day),
        isToday: isSameDay(new Date(), day),
        isSameMonth: isSameMonth(date.value, day),
      })),
      rows: eventsByRow?.length,
      events:
        eventsByRow?.reduce(
          (
            eventsSoFar: ICalendarEvent[],
            currentRow: ICalendarEvent[],
            currentRowIndex: number,
          ) => {
            return [
              ...eventsSoFar,
              ...currentRow.map((event) => ({
                ...event,
                row: currentRowIndex,
              })),
            ]
          },
          [],
        ) || [],
    }
  })
})

const computedMonthAndYearLabel = computed(() => {
  const localizedMonths = useLocalizedCalendarMonths()
  const month = localizedMonths?.[getMonth(date.value)]
  const year = getYear(date.value)
  return `${month} ${year}`
})

const setViewMode = (mode: 'keyDates' | 'calendar') => {
  if (viewMode.value !== mode) {
    searchQuery.value = ''
    selectedBrandFilter.value = ''
    selectedProductFilter.value = ''
  }
  viewMode.value = mode
}

const handleSearchKeyDates = (val: string) => {
  if (val === searchQuery.value) return

  keyDatesLoading.value = true
  keyDatesResults.value = []

  searchQuery.value = val || ''
  currentPage.value = 1

  fetchKeyDates()
}

const handleLoadMoreKeyDates = () => {
  currentPage.value = currentPage.value + 1
  fetchKeyDates()
}

const handleNextMonthClick = () => {
  const currentDate = add(date.value, {
    months: 1,
  })
  date.value = currentDate
}

const handlePreviousMonthClick = () => {
  const currentDate = sub(date.value, {
    months: 1,
  })
  date.value = currentDate
}

const onMouseenter = (path: string) => (activeEventPath.value = path)

const onMouseleave = () => (activeEventPath.value = '')

watch([selectedBrandFilter, selectedProductFilter], () => {
  keyDatesLoading.value = true
  keyDatesResults.value = []

  currentPage.value = 1
})

onMounted(() => {
  date.value = new Date()
})
</script>

<style lang="scss" module>
.wrap {
  position: relative;
  background: var(--white);

  --rows: 1;
  --calendar-event-height: 32px;

  @include media('sm') {
    --calendar-event-height: 50px;
  }
}

.inner {
  max-width: 1350px;
  padding: 45px 20px 60px;
  margin: auto;
}

.keyDatesHeader,
.calendarHeader {
  margin-bottom: 36px;
}

.calendarHeader {
  text-align: center;
}

.heading {
  @include fluid-type(32px, 52px);
  font-family: var(--roboto-condensed);
  line-height: math.div(54, 52);
  color: var(--black);
  margin-bottom: 19px;
}

.subheader {
  @include fluid-type(16px, 22px);
  font-family: var(--georgia);
  line-height: math.div(32, 22);
}

.copy {
  line-height: math.div(30, 19);
  color: var(--gray-8);
  font-size: 14px;

  i,
  em {
    color: hsl(0, 0%, 51%);
    font-size: 17px;
    font-family: --georgia;
    font-style: italic;
  }

  h6 {
    @include fluid-type(20px, 25px);
    line-height: math.div(30px, 25px);
  }
}

.monthHeaderLayout {
  display: grid;
  gap: 20px;
  justify-content: center;
  align-items: center;
  padding: 20px 17px;

  @include media('sm') {
    grid-template-columns: 1fr auto 1fr;
  }
}

.monthHeaderBlockCenter {
  text-align: center;
  display: flex;
  align-items: center;
  order: 9999;

  @include media('sm') {
    order: 0;
  }
}

.monthHeaderBlockRight {
  @include media('sm') {
    text-align: right;
  }
}

.calendar {
  display: grid;
  grid-template-columns: 1fr;
  border: 1px solid #cbcbcb;
}

.grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #cbcbcb;
  border-bottom: 1px solid #cbcbcb;
}

.dayHeader {
  padding: 5px;
  background: #f5f5f5;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  line-height: 15px;

  @include media('sm') {
    padding: 10px;
    font-size: 17px;
    line-height: 18px;
  }
}

.day {
  background: #fff;
  padding: 5px;
  color: #828282;
  font-size: 12px;
  line-height: 1;
  text-align: right;
  height: 100%;
  display: flex;
  justify-content: flex-end;

  & + & {
    border-left: 1px solid #cbcbcb;
  }

  @include media('sm') {
    padding: 10px 5px;
    font-size: 17px;
  }
}

.dayTag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.today {
  background: var(--purple);
  color: #fff;
}

.differentMonth {
  background: var(--gray-2);
}

.week {
  position: relative;
  height: calc((var(--rows) + 1.5) * var(--calendar-event-height));
  min-height: calc(3 * var(--calendar-event-height));
}

.monthPaging {
  color: var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  width: 48px;
  height: 48px;
  color: #fff;
  background: var(--purple);
  border-radius: 50%;
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;

  &:hover,
  &:active {
    background: var(--purple-light);
  }
}

.monthLabel {
  margin: 0 28px;
  min-width: 250px;
  text-align: center;
}

.iconArrow {
  height: 17px;
  width: 11px;
}

.event {
  position: absolute;
  top: var(--calendar-event-height);
  left: 0;
}

.filters {
  display: flex;
  margin-block: 20px;
  gap: 10px;
  flex-wrap: wrap;
}

.select,
.search {
  width: 100%;

  @include media('sm') {
    max-width: 250px;
  }
}

.search {
  @include media('sm') {
    max-width: 250px;
    min-width: 140px;
    flex: 1;
  }
}

.checkboxInput {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 0;

  input {
    margin-left: 5px;
    transform: scale(1.5);
    accent-color: var(--purple-dark);

    &:hover {
      accent-color: var(--purple-light);
    }
  }

  label {
    font-size: 1.125rem;
    font-family: var(--roboto-condensed);
  }

  @include media('sm') {
    padding: 0;
  }
}

.viewModes {
  display: flex;
  width: 100%;

  .viewMode:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .viewMode:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  @include media('sm') {
    width: auto;
    margin-left: auto;
  }
}

.viewMode {
  all: unset;
  box-sizing: border-box;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  width: 100%;
  border-radius: var(--border-radius);
  border: 1px solid #d1d5db;
  background: var(--gray-1);
  color: var(--purple);
  font-weight: 700;
  font-size: to-rem(18px);
  font-family: var(--roboto-condensed);

  &:hover {
    border-color: transparent;
    background: var(--purple-light);
    color: var(--white);
    cursor: pointer;
  }

  &.active {
    border-color: transparent;
    background: var(--purple);
    color: var(--white);

    &::after {
      content: '';
      display: block;
      position: absolute;
      bottom: -20px;
      background: var(--purple);
      width: 20px;
      aspect-ratio: 1;
      clip-path: polygon(100% 0, 0 0, 50% 50%);
    }

    &:hover {
      background: var(--purple-light);

      &::after {
        background: var(--purple-light);
      }
    }
  }

  @include media('sm') {
    min-width: 255px;
  }

  @include media('lg') {
    min-width: clamp(14.375rem, 8.333vw + 8.125rem, 15.625rem);
  }
}

.progress,
.noResults {
  padding: 50px;
  text-align: center;
}

.keyDates {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 40px;
}

.keyDate {
  display: flex;
  flex-direction: column;
  padding: 18px;
  background: var(--gray-1);
  gap: 8px;

  @include media('md') {
    flex-direction: row;
    align-items: center;
    gap: 14px;
  }
}

.keyDateContent {
  display: flex;
  align-items: flex-start;
  width: 100%;

  @include media('md') {
    align-items: center;
  }
}

.keyDateHeaderWrap {
  display: flex;
  align-items: center;
  width: 100%;
}

.calendarIcon {
  width: 70px;
  min-width: 70px;
  aspect-ratio: 1;
  text-align: center;
  margin-right: 24px;
  background: var(--white);

  @include media('md') {
    margin-right: 48px;
  }
}

.calendarMonth {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 25px;
  font-weight: 700;
  text-transform: uppercase;
  font-family: var(--roboto-condensed);
  background: var(--purple);
  color: var(--white);
}

.calendarDay {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: to-rem(36px);
  line-height: to-rem(40px);
}

.eventIcon {
  margin-right: 18px;
  min-width: 48px;
  max-width: 48px;
  object-fit: contain;
}

.keyDateName {
  margin-bottom: 4px;
  font-weight: 700;
  font-size: to-rem(24px);
  font-family: var(--roboto-condensed);
}

.keyDateDescription {
  color: var(--gray-8);
}

.keyDateLinks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 94px;

  @include media('sm') {
    flex-direction: row;
    align-items: center;
    margin-left: 100px;
  }

  @include media('md') {
    flex-direction: column;
    margin-left: 0;
  }

  @include media('lg') {
    flex-direction: row;
    gap: 14px;
  }
}

// just hide links to keep consistent widths
.hiddenLink {
  display: none !important;

  @include media('lg') {
    display: block !important;
    visibility: hidden !important;
    pointer-events: none;
  }
}
</style>
